import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Avatar, 
  Button, 
  TextField, 
  Grid, 
  Chip,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { useUser } from '../context/UserContext';
import { authService } from '../services/apiServices';
import { sanitizeInput } from '../utils/validationUtils';
import { validatePasswordStrength, PASSWORD_REQUIREMENTS_TEXT } from '../utils/passwordUtils';

const Account = () => {
  const { user, loading } = useUser();

  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const handlePasswordChangeSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!currentPassword) {
      setPasswordError('Current password is required.');
      return;
    }

    if (!newPassword) {
      setPasswordError('New password is required.');
      return;
    }

    // Strong password validation
    const passwordValidation = validatePasswordStrength(newPassword);
    if (!passwordValidation.isValid) {
      setPasswordError(passwordValidation.message);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirm password do not match.');
      return;
    }

    setSavingPassword(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      setPasswordSuccess('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      let errorMessage = 'Failed to change password. Please verify your current password and try again.';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setPasswordError(errorMessage);
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return <Typography>Loading account data...</Typography>;
  }

  if (!user) {
    return <Typography>Please log in to view your account details.</Typography>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Account Settings
      </Typography>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Avatar sx={{ width: 100, height: 100, mb: 2 }}>
                {user.username ? sanitizeInput(user.username.charAt(0).toUpperCase()) : 'AD'}
              </Avatar>
              <Typography variant="h6">{sanitizeInput(user.username || 'Admin User')}</Typography>
              <Typography variant="body2" color="text.secondary">{sanitizeInput(user.email || 'admin@example.com')}</Typography>
              <Button variant="outlined" startIcon={<EditIcon />} sx={{ mt: 2 }}>
                Change Avatar
              </Button>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <Typography variant="h6" gutterBottom>
              Profile Information
            </Typography>
            <TextField fullWidth label="Username" value={sanitizeInput(user.username || "")} margin="normal" disabled />
            <TextField fullWidth label="Email" value={sanitizeInput(user.email || "admin@example.com")} margin="normal" disabled />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Change Password
            </Typography>

            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPasswordError('')}>
                {passwordError}
              </Alert>
            )}

            {passwordSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPasswordSuccess('')}>
                {passwordSuccess}
              </Alert>
            )}

            <Box component="form" onSubmit={handlePasswordChangeSubmit}>
              <TextField 
                fullWidth 
                label="Current Password" 
                type={showCurrentPassword ? 'text' : 'password'} 
                margin="normal"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                disabled={savingPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle current password visibility"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                      >
                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField 
                fullWidth 
                label="New Password" 
                type={showNewPassword ? 'text' : 'password'} 
                margin="normal"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={savingPassword}
                helperText={PASSWORD_REQUIREMENTS_TEXT}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle new password visibility"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField 
                fullWidth 
                label="Confirm New Password" 
                type={showConfirmPassword ? 'text' : 'password'} 
                margin="normal"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={savingPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Box mt={2}>
                <Button 
                  type="submit"
                  variant="contained" 
                  startIcon={savingPassword ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />} 
                  color="primary"
                  disabled={savingPassword}
                >
                  {savingPassword ? 'Changing Password...' : 'Change Password'}
                </Button>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Your Permissions
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {user.permissions && user.permissions.length > 0 ? (
                user.permissions.map((permission, index) => (
                  <Chip key={index} label={sanitizeInput(permission)} color="primary" variant="outlined" />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">No permissions assigned.</Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Account;